/**
 * Quote Error Application Service
 *
 * Centralized error classification and toast handling for quote operations
 * Singleton pattern ensures consistent error handling across components
 *
 * @module QuoteErrorApplicationService
 */

import { classifyQuoteError } from "../../../../lib/error-classifier";
import { quoteToast } from "../../../../lib/toast-utils";
import { QuoteErrorType, QuoteErrorDetail } from "../../../../lib/error-types";

/**
 * Application Service for centralized error handling
 * Provides consistent error classification and user feedback
 */
export class QuoteErrorApplicationService {
  private static instance: QuoteErrorApplicationService;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get singleton instance
   */
  static getInstance(): QuoteErrorApplicationService {
    if (!QuoteErrorApplicationService.instance) {
      QuoteErrorApplicationService.instance =
        new QuoteErrorApplicationService();
    }
    return QuoteErrorApplicationService.instance;
  }

  /**
   * Classify error and show error toast
   * @param error - Error to classify
   * @param defaultType - Default error type if classification fails
   * @returns Classified error detail
   */
  classifyAndHandle(
    error: unknown,
    defaultType: QuoteErrorType = QuoteErrorType.UNKNOWN,
  ): QuoteErrorDetail {
    const detail = classifyQuoteError(error, defaultType);
    quoteToast.error(detail);
    return detail;
  }

  /**
   * Show success toast for quote submission
   * @param message - Optional custom success message
   */
  showSuccess(message?: string): void {
    if (message) {
      quoteToast.submissionSuccess();
    } else {
      quoteToast.submissionSuccess();
    }
  }

  /**
   * Show success toast for file upload
   * @param fileCount - Number of files uploaded
   */
  showUploadSuccess(fileCount: number): void {
    quoteToast.uploadSuccess(fileCount);
  }

  /**
   * Show custom error toast
   * @param detail - Error detail to display
   */
  showError(detail: QuoteErrorDetail): void {
    quoteToast.error(detail);
  }
}
